import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {useParams} from 'react-router-dom';

import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {BackTo} from 'components/common/BackTo';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {CopyButton} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {Stepper} from 'components/ui-kit/Stepper';
import {Tooltip} from 'components/ui-kit/Tooltip';

import {GDS_STATUS_COLORS} from 'core/constants/statuses';
import {GdsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';

import {permissions$} from 'models/auth';
import {
    errorTabs$,
    gdsCreatingStep$,
    gdsCreatingStepChanged,
    gdsDetail$,
    gdsDetailGate,
    goodsDeclarationDetails$,
    isSaving$,
    isSubmitting$,
    saveGdsChangesClicked,
    submitGdsFx,
} from 'models/documents/gds';
import {saveData$} from 'models/documents/gds/init';
import {colors} from 'theme';

import {UserPermission} from 'types';
import {useClipboard} from 'utils/custom-hooks';

import {GDFirstStep} from './GDFirstStep';
import {GDSecondStep} from './GDSecondStep';
import {
    ContentWrapper,
    HeadContainer,
    HeadControl,
    Headline,
    Id,
    SpinnerLayer,
    StatusWrapper,
} from './styled';

export const GDNew = () => {
    const isSaving = useStore(isSaving$);
    const isSubmitting = useStore(isSubmitting$);
    const gdsDetail = useStore(gdsDetail$);
    const {isLoading, gdsId, gdsStatus} = useStore(goodsDeclarationDetails$);
    const {hasInfoChanges, hasCodeListChanges} = useStore(saveData$);
    const errorTabs = useStore(errorTabs$);
    const permissions = useStore(permissions$);
    const {id: rawId}: {id: string} = useParams();
    useGate(gdsDetailGate, {id: rawId});
    const {t} = useTranslation();
    const step = useStore(gdsCreatingStep$);
    const copy = useClipboard();
    const copyCode = () => {
        if (!gdsId) return;
        copy(gdsId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const allowToEdit = useMemo(() => {
        if (!permissions?.includes(UserPermission.DOCUMENTS_DECLARATION_EDIT)) {
            return false;
        }

        if (!gdsId) return true;

        return gdsStatus === GdsStatus.DRAFT || gdsStatus === GdsStatus.SUBMITTED;
    }, [gdsStatus, gdsId, permissions]);

    return (
        <>
            <BackTo to={ROUTE.DOCUMENTS_GDS}>{t('pages.documents.gds.back')}</BackTo>
            {!gdsId || (gdsId && gdsDetail && !isLoading) ? (
                <ContentWrapper>
                    <HeadContainer>
                        <Headline>
                            {t(gdsId ? 'pages.documents.gds.edit' : 'pages.documents.gds.new')}
                        </Headline>
                        {gdsId && (
                            <HeadControl>
                                <Id>{gdsId}</Id>
                                <CopyButton
                                    icon={
                                        <Icon
                                            source={CopyIcon}
                                            width={16}
                                            height={20}
                                            fillColor={colors.textSecondary}
                                        />
                                    }
                                    onClick={copyCode}
                                    hoverIndication={true}
                                    label={t('pages.documents.details.copyId')}
                                    labelPlace={'bottom'}
                                />
                                <StatusWrapper color={GDS_STATUS_COLORS[gdsStatus] ?? 'DEFAULT'}>
                                    {t(`pages.documents.gds.status.${gdsStatus}`)}
                                </StatusWrapper>
                                {allowToEdit && (
                                    <>
                                        <div>
                                            <Button
                                                style={{whiteSpace: 'nowrap'}}
                                                type="button"
                                                onClick={() => {
                                                    saveGdsChangesClicked();
                                                }}
                                                disabled={
                                                    isSaving ||
                                                    (!hasInfoChanges && !hasCodeListChanges)
                                                }
                                                variant={ButtonVariant.Secondary}
                                                size="sm"
                                            >
                                                {t('pages.documents.gds.save')}
                                            </Button>
                                        </div>
                                        {gdsStatus === GdsStatus.DRAFT && (
                                            <PermissionAccess
                                                permissions={[
                                                    UserPermission.DOCUMENTS_DECLARATION_CHANGE_TO_SUBMIT_STATUS,
                                                ]}
                                            >
                                                <div style={{marginLeft: 'auto'}}>
                                                    <Tooltip
                                                        id={'submitGds'}
                                                        place={'left'}
                                                        backgroundColor="white"
                                                        textColor="#111111"
                                                        additionalStyles={
                                                            'box-shadow: 0px 3px 6px rgba(116, 116, 116, 0.63);'
                                                        }
                                                    />
                                                    <Button
                                                        data-tip={
                                                            hasInfoChanges || hasCodeListChanges
                                                                ? t(
                                                                      'pages.documents.gds.messages.submit',
                                                                  )
                                                                : ''
                                                        }
                                                        data-for={'submitGds'}
                                                        type="button"
                                                        disabled={
                                                            isSubmitting ||
                                                            hasInfoChanges ||
                                                            hasCodeListChanges
                                                        }
                                                        onClick={() => submitGdsFx(gdsId)}
                                                        variant={ButtonVariant.Normal}
                                                        size="md"
                                                    >
                                                        {t('pages.documents.gds.firstStep.submit')}
                                                    </Button>
                                                </div>
                                            </PermissionAccess>
                                        )}
                                    </>
                                )}
                            </HeadControl>
                        )}
                    </HeadContainer>
                    <Stepper
                        steps={[
                            {
                                label: t('pages.documents.gds.steps.stepOne'),
                                component: <GDFirstStep allowToEdit={allowToEdit} />,
                            },
                            {
                                label: t('pages.documents.gds.steps.stepTwo'),
                                component: <GDSecondStep allowToEdit={allowToEdit} />,
                            },
                        ]}
                        activeStep={step}
                        errorTabs={errorTabs}
                        isClickable={!!gdsId}
                        clickCallback={gdsId ? gdsCreatingStepChanged : undefined}
                    />
                </ContentWrapper>
            ) : (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}
        </>
    );
};
